import {asyncNoop, noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {makeReactContext} from 'lib/makeReactContext';
import VersionHistoryPanel from 'notebook/version-history/VersionHistoryPanel';
import {isOpenVersionLatestDraftVersion} from 'notebook/version-history/VersionHistoryPanelUtil';
import {Version} from 'notebook/version-history/VersionHistoryTypes';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import useOnMount from 'ui-library/hooks/useOnMount';

export interface HistoryManagerContextValue {
  fetchedVersions: null | Array<Version>;
  fetchNow: () => Promise<void>;
  versionIdToView?: string;
  setVersionIdToView: (versionId: string) => void;
}

/**
 * Compares two history contexts. Used as a custom equality function for makeReactContext,
 * since we can be smarter about comparing versions than a full deep equality checker.
 *
 * @package Exported for testing
 */
export function areHistoryContextsEqual(
  context1: HistoryManagerContextValue,
  context2: HistoryManagerContextValue
): boolean {
  const {fetchedVersions: fetchedVersions1} = context1;
  const {fetchedVersions: fetchedVersions2} = context2;
  if (
    context1.versionIdToView !== context2.versionIdToView ||
    (fetchedVersions1 == null) !== (fetchedVersions2 == null)
  ) {
    return false;
  }
  if (fetchedVersions1 == null) {
    return true;
  }
  if (fetchedVersions1.length !== fetchedVersions2!.length) {
    return false;
  }
  return fetchedVersions1.every(
    (version, index) => version.apiIdentifier === fetchedVersions2![index].apiIdentifier
  );
}

export const HistoryManagerContext = makeReactContext<HistoryManagerContextValue>(
  () => ({fetchNow: asyncNoop, fetchedVersions: [], setVersionIdToView: noop}),
  'HistoryManagerContext',
  areHistoryContextsEqual
);

interface ProcedureHistoryPanelAndButtonProps {
  beforeOpeningPanel: () => Promise<void>;
  onCreateDraft: (versionApiIdentifier: string) => Promise<void>;
  doesProcedureHaveUnsavedChanges: boolean;
}

export function ProcedureHistoryPanelButton(props: ProcedureHistoryPanelAndButtonProps): JSX.Element {
  return (
    <div className={css(styles.historyButtonWrapper)}>
      <Float
        interaction={FloatingInteraction.CLICK}
        placement={FloatingPlacement.BOTTOM_END}
        renderContent={() => <ProcedureHistoryPanel {...props} />}
        floatingElementOptions={{floatingBodyClassName: css(styles.floatBody)}}
        dataTestAttrs={{component: 'ProcedureHistoryPanelFloat', element: 'historyTriggerButton'}}
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          aria-label="History"
          icon="clock-outline"
          size={ButtonSize.MEDIUM}
          dataTestAttrs={{component: 'TriggerProcedureHistoryPanelButton', element: 'historyTriggerButton'}}
        />
      </Float>
    </div>
  );
}

export function ProcedureHistoryPanel(props: ProcedureHistoryPanelAndButtonProps): JSX.Element | null {
  const {fetchNow, fetchedVersions, versionIdToView, setVersionIdToView} = HistoryManagerContext.useContext();

  useOnMount(() => {
    async function prep(): Promise<void> {
      await props.beforeOpeningPanel();
      await fetchNow();
    }
    prep();
  });

  const openVersion =
    versionIdToView != null ? fetchedVersions?.find((v) => v.apiIdentifier === versionIdToView) : null;
  const openVersionId = openVersion?.id ?? fetchedVersions?.[0]?.id ?? undefined;

  const isLatestDraftVersion =
    fetchedVersions && openVersionId && isOpenVersionLatestDraftVersion(fetchedVersions, openVersionId);
  // In practice, the procedure will never be dirty, because we hard-save on opening the panel
  const enableResetToVersion = !(isLatestDraftVersion || props.doesProcedureHaveUnsavedChanges);

  return fetchedVersions ? (
    <div className={css(styles.panelWrapper)}>
      <VersionHistoryPanel
        versions={fetchedVersions}
        openVersionId={openVersionId}
        navigateAndSetOpenVersionId={(versionId: number) => {
          const v = fetchedVersions.find((version) => version.id === versionId);
          if (v) {
            assert(v.apiIdentifier != null, 'apiIdentifier must exist for publishable version', {
              failedVersion: v,
            });
            setVersionIdToView(v.apiIdentifier);
          }
        }}
        enableResetToVersion={enableResetToVersion}
        resetToVersion={async ({apiIdentifier}: Version) => {
          // The procedure has already been saved when the panel opened
          if (apiIdentifier) {
            await props.onCreateDraft(apiIdentifier);
          }
        }}
        enableDuplicateFromVersion={false}
        duplicateFromVersion={noop}
        unsaved={false}
      />
    </div>
  ) : null;
}

const styles = StyleSheet.create({
  historyButtonWrapper: {
    position: 'relative',
  },
  panelWrapper: {
    height: '600px',
  },
  floatBody: {
    zIndex: 1,
    width: '250px',
    background: '#fff',
    borderRadius: '4px',
    boxShadow: '0 2px 6px 0 rgba(162, 185, 190, 0.6)',
  },
});
